// src/services/exportHistory.js — the record of every chat export, shared.
//
//   users/{uid}/chatExports/{id} = {
//     chatId, chatName, at (ms), bytes, files, format, platform
//   }
//
// A NEW collection, and deliberately account-level rather than per-device:
// "when did I last export this chat, and how big was it" is a fact about the
// account, not about the handset that happened to run it. The phone writes
// here too (services/chatExport.ts, patched alongside this), so one list shows
// exports made anywhere.
//
// Nothing about the export's CONTENT is stored — only that it happened. The
// file itself never leaves the device that made it.

import {
  collection, doc, addDoc, deleteDoc, onSnapshot, query, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const col = (uid) => collection(db, 'users', uid, 'chatExports');

export function watchExports(uid, cb, { chatId = null, max = 50 } = {}) {
  if (!uid) { cb([]); return () => {}; }
  const q = query(col(uid), orderBy('at', 'desc'), limit(max));
  return onSnapshot(q, (s) => {
    let rows = s.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (chatId) rows = rows.filter((r) => r.chatId === chatId);
    cb(rows);
  }, () => cb([]));
}

export function recordExport(uid, { chatId, chatName, bytes, files, format, platform = 'web' }) {
  return addDoc(col(uid), {
    chatId: String(chatId || ''),
    chatName: String(chatName || ''),
    at: Date.now(),                 // a plain number: both clients sort on it
    bytes: Number(bytes) || 0,
    files: Number(files) || 0,
    format: String(format || 'html'),
    platform,
  });
}

export const deleteExportRecord = (uid, id) => deleteDoc(doc(db, 'users', uid, 'chatExports', id));

export function humanBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = b / 1024; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}
